import { ReactNode, useCallback, useMemo, useContext } from "react";
import { LockForecastContext } from "./context";
import { LockForecastContextType } from "./type";
import { useApi } from "../app/provider";
import { useRefetchCustomerSettings } from "../app/provider";
import { SysMessagesContext } from "../sys_messages/context";
import { SysMessageLevel } from "../sys_messages/type";
import {
  useCopyForecastToDefaultSettingsMutation,
  useUnlockForecastFromDefaultSettingsMutation,
} from "../../services/api/__generated__/backend_gateway-types";

const { Provider } = LockForecastContext;

export const LockForecastProvider = (props: {
  children: ReactNode;
}): JSX.Element => {
  const { apiService } = useApi();
  const { refetchCustomerSettings } = useRefetchCustomerSettings();
  const { addSystemMessage } = useContext(SysMessagesContext);

  const [copyForecastToDefaultSettings, { loading: lockLoading }] =
    useCopyForecastToDefaultSettingsMutation({
      client: apiService.getClient(),
      onCompleted: (data) => {
        console.log(
          "[LockForecastProvider] Lock mutation completed successfully",
          {
            data,
            timestamp: new Date().toISOString(),
          }
        );
        addSystemMessage({
          level: SysMessageLevel.SUCCESS,
          message: "Forecast locked successfully",
        });
        refetchCustomerSettings();
      },
      onError: (error) => {
        console.error("[LockForecastProvider] Lock mutation error", {
          error,
          errorMessage: error.message,
          errorGraphQLErrors: error.graphQLErrors,
          errorNetworkError: error.networkError,
          timestamp: new Date().toISOString(),
        });
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: `Failed to lock forecast: ${error.message}`,
        });
      },
    });

  const [unlockForecastFromDefaultSettings, { loading: unlockLoading }] =
    useUnlockForecastFromDefaultSettingsMutation({
      client: apiService.getClient(),
      onCompleted: (data) => {
        console.log(
          "[LockForecastProvider] Unlock mutation completed successfully",
          {
            data,
            timestamp: new Date().toISOString(),
          }
        );
        addSystemMessage({
          level: SysMessageLevel.SUCCESS,
          message: "Forecast unlocked successfully",
        });
        refetchCustomerSettings();
      },
      onError: (error) => {
        console.error("[LockForecastProvider] Unlock mutation error", {
          error,
          errorMessage: error.message,
          errorGraphQLErrors: error.graphQLErrors,
          errorNetworkError: error.networkError,
          timestamp: new Date().toISOString(),
        });
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: `Failed to unlock forecast: ${error.message}`,
        });
      },
    });

  const isLockUnlockLoading = useMemo(
    () => lockLoading || unlockLoading,
    [lockLoading, unlockLoading]
  );

  const lockForecast = useCallback(
    async (
      sourceInputId: string,
      sourceSettingsNameAndVersion: string,
      sourceRunId: string
    ) => {
      console.log("[LockForecastProvider] lockForecast called", {
        sourceInputId,
        sourceSettingsNameAndVersion,
        sourceRunIdLength: sourceRunId?.length,
        timestamp: new Date().toISOString(),
      });
      try {
        console.log(
          "[LockForecastProvider] Calling copyForecastToDefaultSettings mutation..."
        );
        const result = await copyForecastToDefaultSettings({
          variables: {
            sourceInputId,
            sourceSettingsNameAndVersion,
            sourceRunId,
          },
        });
        console.log(
          "[LockForecastProvider] copyForecastToDefaultSettings mutation call completed",
          {
            result,
            timestamp: new Date().toISOString(),
          }
        );
      } catch (error) {
        // Error handling is done in the mutation's onError callback
        console.error("[LockForecastProvider] Exception in lockForecast:", {
          error,
          errorMessage: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
        });
      }
    },
    [copyForecastToDefaultSettings]
  );

  const unlockForecast = useCallback(async () => {
    console.log("[LockForecastProvider] unlockForecast called", {
      timestamp: new Date().toISOString(),
    });
    try {
      console.log(
        "[LockForecastProvider] Calling unlockForecastFromDefaultSettings mutation..."
      );
      const result = await unlockForecastFromDefaultSettings();
      console.log(
        "[LockForecastProvider] unlockForecastFromDefaultSettings mutation call completed",
        {
          result,
          timestamp: new Date().toISOString(),
        }
      );
    } catch (error) {
      // Error handling is done in the mutation's onError callback
      console.error("[LockForecastProvider] Exception in unlockForecast:", {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString(),
      });
    }
  }, [unlockForecastFromDefaultSettings]);

  const value: LockForecastContextType = {
    lockLoading,
    unlockLoading,
    isLockUnlockLoading,
    copyForecastToDefaultSettings,
    unlockForecastFromDefaultSettings,
    lockForecast,
    unlockForecast,
  };

  return <Provider value={value}>{props.children}</Provider>;
};
